import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from './error-boundary';
import type { NavKey } from './icons';
import { applyTheme, readChoice, resolveTheme, saveChoice, type ThemeChoice } from './theme';
import type {
  CardContent,
  MedScreenContent,
  ObsDayContent,
  PracticeContent,
  SafetyScreeningContent,
  ScreenContent,
} from '@lma/shared';
import * as api from './api';
import { ApiError, type MeasureResult, type ObservationResult, type Resolved } from './api';
import { Nav, ScreenSkeleton } from './components';
import * as S from './screens';
import * as O from './screens-obs';
import {
  ConsultCard,
  PassportCard,
  ProgramCard,
  type ProductKind,
  type ProductOwnership,
} from './screens-products';
import * as D from './screens-day';
import { initTelegramChrome, isInsideTelegram } from './telegram';

/**
 * Состояние прохождения.
 *
 * Правило спеки без исключений: у каждой кнопки ровно один заранее определённый
 * экран назначения. Никаких «вернуться назад» — переходы описаны здесь явно,
 * а карта переходов в @lma/shared проверяет их согласованность тестами.
 */
type View =
  | { id: 'W-01' }
  | { id: 'W-01A' }
  | { id: 'W-02' }
  | { id: 'W-03' }
  | { id: 'W-03A' }
  | { id: 'RETURN' }
  | { id: 'HOME' }
  | { id: 'S-01' }
  | { id: 'C'; cardKey: string }
  | { id: 'C-MED'; cardKey: string }
  | { id: 'M-BEFORE'; cardKey: string }
  | { id: 'P'; cardKey: string }
  | { id: 'M-AFTER'; cardKey: string }
  | { id: 'C-Q'; cardKey: string; value: number }
  | { id: 'R'; cardKey: string; result: MeasureResult }
  | { id: 'WORSE' }
  | { id: 'O-00' }
  | { id: 'O-DAY' }
  | { id: 'O-DAY-OK' }
  | { id: 'O-SKIP' }
  | { id: 'O-RESULT'; result: ObservationResult }
  | { id: 'O-PROGRESS'; result: ObservationResult; currentDay: number; opensAt: string | null }
  | { id: 'O-ROUTE'; showProgram: boolean }
  | { id: 'ANCHOR' }
  | { id: 'P30-01' }
  | { id: 'LPF-01' }
  | { id: 'LPF-CITY' }
  | { id: 'LPF-WA'; template: string }
  | { id: 'LPF-FAR' }
  | { id: 'CLUB-01' }
  | { id: 'CLUB-PROGRAM' }
  | { id: 'CLUB-WAIT' }
  | { id: 'PRODUCT'; product: ProductKind }
  | { id: 'CONSULT-WA'; template: string }
  | { id: 'CLUB-TARIFF' }
  | { id: 'CLUB-TERMS' }
  | { id: 'CLUB-MINE' }
  | { id: 'MY-RESULT' }
  | { id: 'SAFETY' }
  | { id: 'LEGAL' }
  | { id: 'LEGAL-OFFER' }
  | { id: 'LEGAL-PRIVACY' }
  | { id: 'SUPPORT' }
  | { id: 'DAY' }
  | { id: 'DAY-LOG' }
  | { id: 'ASK' }
  | { id: 'ASK-OK' }
  | { id: 'MORNING' };

/** Экраны входа: нижнего меню на них нет. */
const WITHOUT_NAV = new Set(['W-01', 'W-01A', 'W-02', 'W-03', 'W-03A', 'RETURN']);

/**
 * Тексты, которые не тащим в каждую сессию: документы читают редко,
 * а весят они пять сотен абзацев. Всё остальное приходит одним запросом.
 */
const ON_DEMAND = new Set<string>(['LEGAL-OFFER', 'LEGAL-PRIVACY']);

const NAV_EXTRA: { key: NavKey; label: string }[] = [
  // Дом первым: с него начинается любое возвращение, и уйти с него
  // навсегда было бы странно.
  { key: 'HOME', label: 'Главная' },
  { key: 'consult222', label: 'Онлайн-консультация' },
  { key: 'lpf', label: 'Терапия' },
  { key: 'SAFETY', label: 'Безопасность' },
  { key: 'DAY-LOG', label: 'Мой день' },
  { key: 'ASK', label: 'Задать вопрос' },
  { key: 'SUPPORT', label: 'Поддержка' },
  { key: 'LEGAL', label: 'Документы' },
  { key: 'MY-RESULT', label: 'Мой результат' },
];

export function App() {
  const [view, setView] = useState<View | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [outside, setOutside] = useState(false);

  // Содержание. Ничего из этого не зашито в код.
  const [screens, setScreens] = useState<Record<string, ScreenContent>>({});
  const [labels, setLabels] = useState<S.Labels>({});
  const [cards, setCards] = useState<Resolved<CardContent>[]>([]);
  const [screening, setScreening] = useState<SafetyScreeningContent | null>(null);
  const [card, setCard] = useState<CardContent | null>(null);
  const [cardKey, setCardKey] = useState<string | null>(null);
  const [practice, setPractice] = useState<PracticeContent | null>(null);
  const [med, setMed] = useState<MedScreenContent | null>(null);
  const [obsDay, setObsDay] = useState<ObsDayContent | null>(null);
  const [obsMarker, setObsMarker] = useState<api.ObservationMarker | null>(null);
  const [productOwned, setProductOwned] = useState<ProductOwnership>({
    passport14: false,
    stage1: false,
    full: false,
    preenrolled: false,
  });
  // Клуб: выбранный тариф, его условия и своя подписка.
  // По умолчанию средний: он и есть рекомендуемый.
  const [tariff, setTariff] = useState<api.ClubTariff>('club_2');
  const [terms, setTerms] = useState<api.PaymentTerms | null>(null);
  const [clubPrices, setClubPrices] = useState<Record<string, api.PaymentTerms | undefined>>({});
  const [subscriptions, setSubscriptions] = useState<api.Subscription[]>([]);
  const [home, setHome] = useState<api.HomeData | null>(null);
  const [myNotes, setMyNotes] = useState<api.SelfNote[]>([]);
  const [myDayBase, setMyDayBase] = useState<Record<string, { label: string; value: string }[]>>(
    {},
  );

  // Интенсив: цена, наличие доступа и ссылка на канал.
  const [intensiveTerms, setIntensiveTerms] = useState<api.PaymentTerms | null>(null);
  const [hasIntensive, setHasIntensive] = useState(false);
  const [intensiveChannel, setIntensiveChannel] = useState<string | null>(null);
  const [theme, setTheme] = useState<ThemeChoice>(() => readChoice());
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readChoice()));
  const [payBusy, setPayBusy] = useState(false);
  const [askBusy, setAskBusy] = useState(false);
  const [dayBusy, setDayBusy] = useState(false);
  const [today, setToday] = useState<{ localDate: string; entry: api.DayEntry | null } | null>(
    null,
  );
  const [dayWeek, setDayWeek] = useState<api.DayOfWeek[]>([]);
  const [dayMonth, setDayMonth] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [dayCalendar, setDayCalendar] = useState<(api.DayEntry & { date: string })[]>([]);
  const [dayEarliest, setDayEarliest] = useState<string | null>(null);
  /** Имя из Telegram — только как подсказка в поле: его можно поправить. */
  const [firstName, setFirstName] = useState('');

  const [links, setLinks] = useState<{ whatsapp: string | null; support: string | null }>({
    whatsapp: null,
    support: null,
  });
  const [myMeasurements, setMyMeasurements] = useState<api.MyMeasurement[]>([]);

  /*
   * Открытие экрана пишется здесь, в одном месте.
   *
   * `setView` вызывается из шести десятков мест, и разметить каждое —
   * значит гарантированно забыть половину и заново забывать при каждой
   * правке. Экран — это одно состояние, и следить надо за ним, а не за
   * теми, кто его меняет.
   *
   * Вместе с экраном пишем предыдущий: пара «откуда → куда» и есть переход.
   * Отдельного события на нажатие кнопки не нужно — карта экранов и так
   * держит правило «одна кнопка — один экран назначения».
   */
  const previousScreen = useRef<string | null>(null);

  useEffect(() => {
    const id = view?.id;
    if (!id) return;
    // Перерисовка того же экрана — не переход: карточка меняется, экран нет.
    if (previousScreen.current === id) return;

    api.trackEvent('screen_opened', { screen: id, from: previousScreen.current ?? '' });
    previousScreen.current = id;
  }, [view?.id]);

  /** База дня: сегодняшняя запись и история. Грузим по требованию. */
  const openDay = useCallback(async () => {
    setToday(await api.fetchToday());
    setView({ id: 'DAY' });
  }, []);

  const loadMonth = useCallback(async (month: string) => {
    const last = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0))
      .toISOString()
      .slice(0, 10);

    const data = await api.fetchDayCalendar(month, last);
    setDayMonth(month);
    setDayCalendar(data.days);
    setDayEarliest(data.earliest);
  }, []);

  const openDayLog = useCallback(async () => {
    const month = new Date().toISOString().slice(0, 8) + '01';
    const [week] = await Promise.all([api.fetchDayWeek(), loadMonth(month)]);
    setDayWeek(week.days);
    setView({ id: 'DAY-LOG' });
  }, [loadMonth]);

  /** Ошибка сервера может нести адрес экрана: стоп-статус уводит на W-03A. */
  /**
   * Отказы, которые не являются авариями.
   *
   * «Следующий день откроется завтра» — правильный ход событий, а не поломка:
   * два задания подряд выполнять нельзя. Показывать это красным экраном
   * «Не удалось начать» с кнопкой «Попробовать снова», которая снова не
   * сработает, — значит пугать женщину за честно сделанную работу.
   */
  const CALM_ERRORS = new Set(['day_not_open_yet', 'day_already_answered', 'wrong_day']);

  const handle = useCallback((error: unknown) => {
    if (error instanceof ApiError && error.redirect === 'W-03A') {
      setView({ id: 'W-03A' });
      return;
    }

    if (error instanceof ApiError && error.code && CALM_ERRORS.has(error.code)) {
      void openHome();
      return;
    }

    setFatal(error instanceof ApiError ? error.message : 'Не удалось связаться с сервером');
  }, []);

  // Тему ставим до первого кадра: иначе видна вспышка чужого фона.
  useEffect(() => {
    setResolved(applyTheme(theme));
  }, [theme]);

  useEffect(() => {
    initTelegramChrome();

    // В браузере подписанных данных запуска нет. В разработке пускаем
    // по отдельному маршруту, чтобы смотреть экраны; в проде его не существует.
    const devMode = import.meta.env.DEV && !isInsideTelegram();

    if (!isInsideTelegram() && !devMode) {
      setOutside(true);
      return;
    }

    void (async () => {
      try {
        const session = devMode ? await api.authenticateDev() : await api.authenticate();

        // Запуск приложения — не то же самое, что /start в боте. Женщина
        // жмёт /start один раз, а заходит потом десятки: без этого события
        // возвраты не видно вообще.
        api.trackEvent('app_opened', { isNew: session.isNew });
        setFirstName(session.profile.firstName ?? '');

        const [loaded, screeningContent, cardList, linkList] = await Promise.all([
          api.fetchAllScreens(),
          api.fetchSafetyScreening(),
          api.fetchCards(),
          api.fetchLinks().catch(() => ({ whatsapp: null, support: null })),
        ]);

        const map: Record<string, ScreenContent> = {};
        for (const screen of loaded.screens) {
          const id = screen.key.replace(/^screen:/, '');
          if (ON_DEMAND.has(id)) continue;
          map[id] = screen.data;
        }

        setScreens(map);
        setLabels(map.COMMON?.buttons ?? {});
        setScreening(screeningContent.data);
        setCards(cardList.cards);
        setLinks(linkList);
        const entry = session.entryScreen as View['id'];

        // Возвращение — это дом, а не экран «маршрут сохранён»: женщина
        // должна увидеть, где остановилась и что сделать сегодня.
        if ((entry as string) === 'O-DAY') {
          await enterObservation();
        } else if (entry === 'RETURN') {
          setToday(await api.fetchToday().catch(() => null));
          setHome(await api.fetchHome().catch(() => null));
          setView({ id: 'HOME' });
        } else {
          setView({ id: entry } as View);
        }
      } catch (error) {
        handle(error);
      }
    })();
  }, [handle]);

  const openCard = useCallback(
    async (key: string) => {
      try {
        await api.selectCard(key);
        const loaded = await api.fetchCard(key);
        setCard(loaded.data);
        setCardKey(key);
        setView({ id: 'C', cardKey: key });
      } catch (error) {
        handle(error);
      }
    },
    [handle],
  );

  /**
   * Карточка продукта. Доступы подтягиваем сразу: от них зависит, какие кнопки показать.
   * Предзапись храним в состоянии экрана: сервер при повторной записи ничего не дублирует.
   */
  const openProduct = useCallback(
    async (product: ProductKind) => {
      try {
        const { accesses } = await api.myAccess();
        const codes = new Set(accesses.map((a) => a.product));
        setProductOwned((prev) => ({
          passport14: codes.has('passport14'),
          stage1: codes.has('prog12_stage1'),
          full: codes.has('prog12_full') || codes.has('prog12_topup'),
          preenrolled: prev.preenrolled,
        }));
        setView({ id: 'PRODUCT', product });
      } catch (error) {
        handle(error);
      }
    },
    [handle],
  );

  /** Оплата: сервер сам проверяет доступ и ограничения; отсюда только переход на страницу оплаты. */
  const payFor = useCallback(
    async (code: 'passport14' | 'prog12_stage1' | 'prog12_full' | 'prog12_topup') => {
      try {
        const { confirmationUrl } = await api.startPayment(code);
        window.location.href = confirmationUrl;
      } catch (error) {
        handle(error);
      }
    },
    [handle],
  );

  /** Канал выдаётся только тем, у кого доступ открыт: сервер проверяет и отдаёт ссылку. */
  const openChannel = useCallback(
    async (code: 'passport14' | 'prog12_stage1' | 'prog12_full') => {
      try {
        const { link } = await api.channelLink(code);
        if (link) window.location.href = link;
        else handle(new Error('Доступ пока не открыт. Если вы оплатили, напишите нам.'));
      } catch (error) {
        handle(error);
      }
    },
    [handle],
  );

  const openObservation = useCallback(async () => {
    try {
      const state = await api.fetchObservation();

      if (state.run?.status === 'completed') {
        const result = await api.fetchObservationResult();
        setView({ id: 'O-RESULT', result });
        return;
      }
      if (state.run && state.day) {
        setObsDay(state.day.data);
        setObsMarker(state.run.marker ?? null);
        setView({ id: 'O-DAY' });
        return;
      }

      /*
        День уже пройден или ещё не открылся — это хорошая новость, а не тупик.
        Ведём домой: там виден прогресс и написано, когда возвращаться.
        Раньше здесь открывалась форма дня, которую сервер потом отказывался
        принять, и женщина получала красный экран после честно сделанной работы.
      */
      if (state.run?.waitingForNextDay) {
        const result = await api.fetchObservationResult();
        setView({
          id: 'O-PROGRESS',
          result,
          currentDay: state.run.currentDay,
          opensAt: state.run.opensAt ?? null,
        });
        return;
      }

      setView({ id: 'O-00' });
    } catch (error) {
      handle(error);
    }
  }, [handle]);

  /**
   * Вход в семь дней для той, у кого ещё нет запроса: сразу день с видео, без промежуточного
   * экрана. Новую серию заводим, только если её нет: завершённая ведёт на Карту, а не заново.
   */
  const enterObservation = useCallback(async () => {
    try {
      const state = await api.fetchObservation();
      if (!state.run) await api.startObservation(null);
      await openObservation();
    } catch (error) {
      handle(error);
    }
  }, [handle, openObservation]);

  const openMyResult = useCallback(async () => {
    try {
      const mine = await api.fetchMyResult();
      setMyMeasurements(mine.measurements);
      setMyNotes(mine.notes ?? []);
      setMyDayBase(mine.dayBase ?? {});
      setView({ id: 'MY-RESULT' });
    } catch (error) {
      handle(error);
    }
  }, [handle]);

  if (outside) {
    return (
      <main className="screen">
        <p className="overline">Разработка</p>
        <h1>Откройте приложение из Telegram</h1>
        <p className="secondary">
          Вне Telegram нет подписанных данных запуска, а доверять чему-то ещё приложение не должно.
          Запустите бота <strong>@love_my_age_bot</strong> и нажмите кнопку.
        </p>
      </main>
    );
  }

  if (fatal) {
    return (
      <main className="screen">
        <h1>Не удалось начать</h1>
        <div className="block-stop">
          <span className="block-label">Что произошло</span>
          <p>{fatal}</p>
        </div>
        <div className="stack actions">
          <button className="btn-secondary" onClick={() => window.location.reload()}>
            Попробовать снова
          </button>
        </div>
      </main>
    );
  }

  if (!view || Object.keys(screens).length === 0) return <ScreenSkeleton />;

  return (
    <>
      <ErrorBoundary key={view.id}>{renderView(view)}</ErrorBoundary>
      {WITHOUT_NAV.has(view.id) ? null : (
        <Nav
          active={null}
          extraItems={NAV_EXTRA}
          resolved={resolved}
          onThemeChange={(next) => {
            saveChoice(next);
            setTheme(next);
          }}
          onSelect={(key) => {
            if (key === 'HOME') void openHome();
            else if (key === 'obs7') void openObservation();
            else if (key === 'intensive21') void openIntensive();
            else if (key === 'lpf') setView({ id: 'LPF-01' });
            else if (key === 'passport14' || key === 'program12' || key === 'consult222')
              void openProduct(key);
            else if (key === 'MY-RESULT') void openMyResult();
            // «Мой день» из меню — это про посмотреть, а не заполнить:
            // заполнять зовёт главная, раз в день и своим приглашением.
            else if (key === 'DAY-LOG') void openDayLog().catch(handle);
            else if (key === 'DAY') void openDay().catch(handle);
            else setView({ id: key as View['id'] } as View);
          }}
        />
      )}
    </>
  );

  /** Документ грузится в момент открытия: держать его в памяти всю сессию незачем. */
  async function openLegal(id: 'LEGAL-OFFER' | 'LEGAL-PRIVACY'): Promise<void> {
    setView({ id });
    if (screens[id]) return;

    try {
      const loaded = await api.fetchScreen(id);
      setScreens((prev) => ({ ...prev, [id]: loaded.data }));
    } catch (error) {
      handle(error);
    }
  }

  /** Дом собирается одним запросом: водопад из четырёх на телефоне заметен. */
  async function openHome(): Promise<void> {
    setView({ id: 'HOME' });

    try {
      // Сегодняшнюю запись тянем вместе с домом: приглашение отметить
      // день должно знать, отмечен он или нет, до первого нажатия.
      const [homeData, todayData] = await Promise.all([
        api.fetchHome(),
        api.fetchToday().catch(() => null),
      ]);

      setHome(homeData);
      setToday(todayData);
    } catch (error) {
      handle(error);
    }
  }

  /**
   * Интенсив: цена и состояние доступа.
   *
   * Спрашиваем сервер, а не решаем в приложении: цену выставляет Елена
   * в панели, и экран обязан показывать то, что там сейчас, а не то,
   * что было верно на момент сборки.
   */
  async function openIntensive(): Promise<void> {
    setView({ id: 'P30-01' });

    try {
      const [terms, access] = await Promise.all([
        api.paymentTerms('intensive21'),
        api.myAccess().catch(() => ({ accesses: [] })),
      ]);

      const open = access.accesses.some((a) => a.product === 'intensive21');
      setIntensiveTerms(terms);
      setHasIntensive(open);

      // Ссылку в закрытый канал сервер отдаёт только тем, у кого доступ открыт.
      if (open) setIntensiveChannel((await api.channelLink('intensive21')).link);
    } catch (error) {
      handle(error);
    }
  }

  /**
   * Уход на оплату интенсива.
   *
   * Доступ откроется по вебхуку, а не по возвращению сюда: женщина может
   * закрыть вкладку до списания или дойти до адреса возврата руками.
   */
  async function payForIntensive(): Promise<void> {
    setPayBusy(true);

    try {
      const { confirmationUrl } = await api.startPayment('intensive21');
      window.location.href = confirmationUrl;
    } catch (error) {
      handle(error);
    } finally {
      setPayBusy(false);
    }
  }

  /** Цены всех тарифов сразу: выбирать вслепую женщина не должна. */
  async function openTariffs(): Promise<void> {
    setView({ id: 'CLUB-TARIFF' });

    try {
      const codes: api.ClubTariff[] = ['club_trial', 'club_1', 'club_2', 'club_3'];
      const loaded = await Promise.all(codes.map((code) => api.paymentTerms(code)));

      const map: Record<string, api.PaymentTerms> = {};
      loaded.forEach((item, i) => {
        map[codes[i]!] = item;
      });

      setClubPrices(map);
      setTerms(map[tariff] ?? null);
    } catch (error) {
      handle(error);
    }
  }

  async function openTerms(code: api.ClubTariff): Promise<void> {
    try {
      const loaded = await api.paymentTerms(code);
      setTerms(loaded);
      setView({ id: 'CLUB-TERMS' });
    } catch (error) {
      handle(error);
    }
  }

  /**
   * Уход на оплату.
   *
   * Доступ откроется по вебхуку, а не по возвращению сюда: женщина может
   * закрыть вкладку до списания или дойти до адреса возврата руками.
   */
  async function payForClub(): Promise<void> {
    setPayBusy(true);

    try {
      const { confirmationUrl } = await api.startPayment(tariff, true);
      window.location.href = confirmationUrl;
    } catch (error) {
      handle(error);
    } finally {
      setPayBusy(false);
    }
  }

  async function openMySubscription(): Promise<void> {
    setView({ id: 'CLUB-MINE' });

    try {
      const { subscriptions: loaded } = await api.mySubscriptions();
      setSubscriptions(loaded);
    } catch (error) {
      handle(error);
    }
  }

  async function cancelRenewal(id: string): Promise<void> {
    const sure = window.confirm(
      'Отключить продление? Доступ сохранится до конца оплаченного периода.',
    );
    if (!sure) return;

    setPayBusy(true);

    try {
      await api.cancelSubscription(id);
      const { subscriptions: loaded } = await api.mySubscriptions();
      setSubscriptions(loaded);
    } catch (error) {
      handle(error);
    } finally {
      setPayBusy(false);
    }
  }

  function goRoute(target: 'intensive21' | 'lpf' | 'obs7') {
    if (target === 'intensive21') setView({ id: 'P30-01' });
    else if (target === 'lpf') setView({ id: 'LPF-01' });
    else void openObservation();
  }

  function renderView(v: View) {
    switch (v.id) {
      case 'W-01':
        return (
          <S.Welcome
            screen={screens['W-01']!}
            onStart={() => setView({ id: 'W-02' })}
            onHowItWorks={() => setView({ id: 'W-01A' })}
          />
        );

      case 'W-01A':
        return (
          <S.HowItWorks
            screen={screens['W-01A']!}
            onStart={() => setView({ id: 'W-02' })}
            onBack={() => setView({ id: 'W-01' })}
          />
        );

      case 'W-02':
        return (
          <S.Boundaries
            screen={screens['W-02']!}
            onContinue={() =>
              void api
                .acceptTerms()
                .then(() => setView({ id: 'W-03' }))
                .catch(handle)
            }
            onBack={() => setView({ id: 'W-01' })}
          />
        );

      case 'W-03':
        return (
          <S.Screening
            content={screening!}
            onAnswer={(hasFlags) =>
              void api
                .submitScreening(hasFlags)
                .then((r) => (r.next === 'W-03A' ? setView({ id: 'W-03A' }) : enterObservation()))
                .catch(handle)
            }
          />
        );

      case 'W-03A':
        return (
          <S.MedicalRoute
            screen={screens['W-03A']!}
            onCleared={() =>
              void api
                .clearSafetyByUser()
                .then(() => enterObservation())
                .catch(handle)
            }
            onRemind={() => setView({ id: 'S-01' })}
          />
        );

      case 'RETURN':
        return (
          <S.Return
            screen={screens.RETURN!}
            onResult={() => void openMyResult()}
            onOther={() => setView({ id: 'S-01' })}
          />
        );

      case 'S-01':
        return <S.SelectRequest screen={screens['S-01']!} cards={cards} onSelect={openCard} />;

      case 'C':
        return (
          <S.Card
            card={card!}
            labels={labels}
            onTry={() => setView({ id: 'M-BEFORE', cardKey: v.cardKey })}
            onMed={() =>
              void api
                .fetchMedScreen(card!.medScreenKey)
                .then((r) => {
                  setMed(r.data);
                  setView({ id: 'C-MED', cardKey: v.cardKey });
                })
                .catch(handle)
            }
            onOther={() => setView({ id: 'S-01' })}
          />
        );

      case 'C-MED':
        return (
          <S.CardMedical
            med={med!}
            labels={labels}
            onBack={() => setView({ id: 'C', cardKey: v.cardKey })}
            onMedicalRoute={() =>
              void api
                .reportWorse('C-MED', v.cardKey)
                .then(() => setView({ id: 'W-03A' }))
                .catch(handle)
            }
          />
        );

      case 'M-BEFORE':
        return (
          <S.Measure
            question={card!.measureBefore}
            step={1}
            labels={labels}
            buttonLabel={labels.next ?? 'Дальше'}
            onSubmit={(value) =>
              void api
                .measureBefore(v.cardKey, value)
                .then(async () => {
                  const loaded = await api.fetchPractice(card!.practiceKey);
                  setPractice(loaded.data);
                  api.trackEvent('practice_opened', { practiceKey: card!.practiceKey });
                  setView({ id: 'P', cardKey: v.cardKey });
                })
                .catch(handle)
            }
          />
        );

      case 'P':
        return (
          <S.Probe
            practice={practice!}
            labels={labels}
            onDone={() => {
              api.trackEvent('practice_done', { practiceKey: card?.practiceKey ?? '' });
              setView({ id: 'M-AFTER', cardKey: v.cardKey });
            }}
            onWorse={() =>
              void api
                .reportWorse('P', v.cardKey)
                .then(() => setView({ id: 'WORSE' }))
                .catch(handle)
            }
          />
        );

      case 'M-AFTER':
        return (
          <S.Measure
            question={card!.measureAfter}
            note={card!.afterNote}
            step={3}
            labels={labels}
            buttonLabel={labels.show_result ?? 'Показать результат'}
            onSubmit={(value) => {
              // У карточки «не могу выбрать» между замером и результатом
              // стоит дополнительный вопрос.
              if (card!.extraQuestion) {
                setView({ id: 'C-Q', cardKey: v.cardKey, value });
                return;
              }
              void api
                .measureAfter(v.cardKey, value)
                .then((r) => setView({ id: 'R', cardKey: v.cardKey, result: r.result }))
                .catch(handle);
            }}
          />
        );

      case 'C-Q':
        return (
          <S.ExtraQuestion
            question={card!.extraQuestion!.title}
            options={card!.extraQuestion!.options}
            onAnswer={(choice) =>
              void api
                .measureAfter(v.cardKey, v.value, choice)
                .then((r) => setView({ id: 'R', cardKey: v.cardKey, result: r.result }))
                .catch(handle)
            }
          />
        );

      case 'R':
        return (
          <S.Result
            card={card!}
            result={v.result}
            labels={labels}
            onSaveNote={async (input) => {
              await api.saveCardNote(v.cardKey, input);
            }}
            onPrimary={() => {
              void api.takeRoute(v.cardKey, card!.primary.target).catch(() => undefined);
              goRoute(card!.primary.target);
            }}
            onSecondary={() => {
              if (!card!.secondary) return;
              void api.takeRoute(v.cardKey, card!.secondary.target).catch(() => undefined);
              goRoute(card!.secondary.target);
            }}
            onOther={() => setView({ id: 'S-01' })}
          />
        );

      case 'WORSE':
        return (
          <S.Worse
            screen={screens.WORSE!}
            onMedicalRoute={() => setView({ id: 'W-03A' })}
            onRest={() => void openMyResult()}
          />
        );

      // ─── Семь дней ────────────────────────────────────────

      case 'O-00':
        return (
          <O.ContentScreen
            screen={screens['O-00']!}
            actions={[
              {
                key: 'start_free',
                label: 'Начать бесплатно',
                kind: 'primary',
                onClick: () =>
                  void api.startObservation(cardKey).then(openObservation).catch(handle),
              },
              {
                key: 'not_now',
                label: 'Не сейчас',
                kind: 'tertiary',
                onClick: () => void openMyResult(),
              },
            ]}
          />
        );

      case 'O-DAY':
        return (
          <O.ObservationDay
            day={obsDay!}
            labels={labels}
            marker={obsMarker}
            onOpenDayLog={() => void openDayLog().catch(handle)}
            onDone={(answers) =>
              void api
                .submitDay(obsDay!.dayIndex, answers)
                .then((r) =>
                  r.completed
                    ? api
                        .fetchObservationResult()
                        .then((result) => setView({ id: 'O-RESULT', result }))
                    : setView({ id: 'O-DAY-OK' }),
                )
                .catch(handle)
            }
            onSkip={() =>
              void api
                .skipDay(obsDay!.dayIndex)
                .then(() => setView({ id: 'O-SKIP' }))
                .catch(handle)
            }
            onWorse={() =>
              void api
                .reportDayWorse(obsDay!.dayIndex)
                .then(() => setView({ id: 'WORSE' }))
                .catch(handle)
            }
          />
        );

      case 'O-DAY-OK':
        return (
          <O.ContentScreen
            screen={screens['O-DAY-OK']!}
            actions={[
              {
                key: 'ok',
                label: 'Хорошо',
                kind: 'primary',
                onClick: () => void openObservation(),
              },
            ]}
          />
        );

      case 'O-SKIP':
        return (
          <O.ContentScreen
            screen={screens['O-SKIP']!}
            actions={[
              {
                key: 'ok',
                label: 'Хорошо',
                kind: 'primary',
                onClick: () => void openObservation(),
              },
            ]}
          />
        );

      case 'PRODUCT': {
        const screenKey = {
          passport14: 'PASSPORT-01',
          program12: 'PROGRAM-01',
          consult222: 'CONSULT-01',
        }[v.product];
        const goBack = () => void openHome();

        if (v.product === 'passport14') {
          return (
            <PassportCard
              screen={screens[screenKey]!}
              owned={productOwned}
              onBuy={() => void payFor('passport14')}
              onOpenChannel={() => void openChannel('passport14')}
              onBack={goBack}
            />
          );
        }

        if (v.product === 'program12') {
          return (
            <ProgramCard
              screen={screens[screenKey]!}
              owned={productOwned}
              onPayFull={() => void payFor('prog12_full')}
              onPayStage1={() => void payFor('prog12_stage1')}
              onTopUp={() => void payFor('prog12_topup')}
              onPreenroll={() =>
                void api
                  .joinProgramWaitlist()
                  .then((r) => {
                    if (!r.opensAt) setProductOwned((prev) => ({ ...prev, preenrolled: true }));
                  })
                  .catch(handle)
              }
              onOpenChannel={() =>
                void openChannel(productOwned.full ? 'prog12_full' : 'prog12_stage1')
              }
              onBack={goBack}
            />
          );
        }

        return (
          <ConsultCard
            screen={screens[screenKey]!}
            onBook={() =>
              void api
                .consultRequest()
                .then((r) => setView({ id: 'CONSULT-WA', template: r.whatsappTemplate }))
                .catch(handle)
            }
            onBack={goBack}
          />
        );
      }

      case 'O-PROGRESS':
        return (
          <O.ObservationProgress
            result={v.result}
            currentDay={v.currentDay}
            opensAt={v.opensAt}
            onOpenDayLog={() => void openDayLog().catch(handle)}
            onHome={() => void openHome()}
          />
        );

      case 'O-RESULT':
        return (
          <O.ObservationResultScreen
            result={v.result}
            onPassport={() => void openProduct('passport14')}
            onConsult={() => void openProduct('consult222')}
            onOpenDayLog={() => void openDayLog().catch(handle)}
            onReplay={() =>
              void api
                .startObservation(null)
                .then(() => {
                  O.forgetDayDrafts();
                  return openObservation();
                })
                .catch(handle)
            }
          />
        );

      case 'O-ROUTE':
        return (
          <O.ObservationRoute
            screen={screens['O-ROUTE']!}
            showProgram={v.showProgram}
            onChoose={(key) => {
              if (key === 'new_symptom') {
                void api
                  .reportWorse('O-ROUTE')
                  .then(() => setView({ id: 'W-03A' }))
                  .catch(handle);
                return;
              }
              if (key === 'pain_or_history') setView({ id: 'LPF-01' });
              else if (key === 'tolerates_practice') setView({ id: 'P30-01' });
              else setView({ id: 'ANCHOR' });
            }}
          />
        );

      case 'ANCHOR':
        return (
          <O.ContentScreen
            screen={screens.ANCHOR!}
            actions={[
              { key: 'ok', label: 'Хорошо', kind: 'primary', onClick: () => void openMyResult() },
            ]}
          />
        );

      // ─── Продукты ─────────────────────────────────────────

      case 'P30-01':
        return (
          <O.Intensive
            screen={screens['P30-01']!}
            terms={intensiveTerms}
            hasAccess={hasIntensive}
            channelUrl={intensiveChannel}
            busy={payBusy}
            onPay={() => void payForIntensive()}
            onBack={() => void openHome()}
          />
        );

      case 'LPF-01':
        return (
          <O.ContentScreen
            screen={screens['LPF-01']!}
            actions={[
              {
                key: 'book',
                label: 'Записаться',
                kind: 'primary',
                onClick: () => setView({ id: 'LPF-CITY' }),
              },
              { key: 'back', label: 'Назад', kind: 'tertiary', onClick: () => void openMyResult() },
            ]}
          />
        );

      case 'LPF-CITY':
        return (
          <O.CityInput
            screen={screens['LPF-CITY']!}
            onSubmit={(city) =>
              void api
                .requestBooking(city, cardKey)
                .then((r) =>
                  r.served
                    ? setView({ id: 'LPF-WA', template: r.whatsappTemplate })
                    : setView({ id: 'LPF-FAR' }),
                )
                .catch(handle)
            }
          />
        );

      case 'LPF-WA':
        return (
          <O.WhatsAppHandoff
            screen={screens['LPF-WA']!}
            template={v.template}
            link={links.whatsapp}
            onOpen={() => api.trackEvent('whatsapp_opened')}
            onBack={() => setView({ id: 'LPF-01' })}
          />
        );

      case 'CONSULT-WA':
        return (
          <O.WhatsAppHandoff
            screen={screens['CONSULT-WA']!}
            template={v.template}
            link={links.whatsapp}
            onOpen={() => api.trackEvent('whatsapp_opened')}
            onBack={() => setView({ id: 'PRODUCT', product: 'consult222' })}
          />
        );

      case 'LPF-FAR':
        return (
          <O.ContentScreen
            screen={screens['LPF-FAR']!}
            actions={[
              {
                key: 'notify_when_available',
                label: 'Сообщить, когда появится',
                kind: 'secondary',
                onClick: () => void openMyResult(),
              },
              {
                key: 'start_obs7',
                label: 'Начать семь дней',
                kind: 'primary',
                onClick: () => void openObservation(),
              },
            ]}
          />
        );

      case 'HOME':
        if (!home) return <ScreenSkeleton />;

        return (
          <O.Home
            screen={screens.HOME!}
            data={home}
            onContinue={() => void openObservation()}
            onMyResult={() => void openMyResult()}
            onChooseRequest={() => setView({ id: 'S-01' })}
            onAsk={() => setView({ id: 'ASK' })}
            onDay={() => void (today?.entry ? openDayLog() : openDay()).catch(handle)}
            dayFilled={Boolean(today?.entry)}
          />
        );

      case 'CLUB-01':
        return (
          <O.ContentScreen
            screen={screens['CLUB-01']!}
            actions={[
              {
                key: 'choose_tariff',
                label: 'Вступить в клуб',
                kind: 'primary',
                onClick: () => void openTariffs(),
              },
              // Пока клуб не продаётся, остаётся прежний путь: сообщить о старте.
              {
                key: 'notify_on_launch',
                label: 'Сообщить о запуске',
                kind: 'secondary',
                onClick: () =>
                  void api
                    .joinProgramWaitlist()
                    .then(() => setView({ id: 'CLUB-WAIT' }))
                    .catch(handle),
              },
              {
                key: 'program',
                label: 'Программа 12 недель',
                kind: 'secondary',
                onClick: () => setView({ id: 'CLUB-PROGRAM' }),
              },
              {
                key: 'my_subscription',
                label: 'Моя подписка',
                kind: 'tertiary',
                onClick: () => void openMySubscription(),
              },
            ]}
          />
        );

      case 'CLUB-PROGRAM':
        return (
          <O.ClubProgram
            screen={screens['CLUB-PROGRAM']!}
            onJoin={() => void openTariffs()}
            onBack={() => setView({ id: 'CLUB-01' })}
          />
        );

      case 'CLUB-TARIFF':
        return (
          <O.ClubTariff
            screen={screens['CLUB-TARIFF']!}
            prices={clubPrices}
            selected={tariff}
            onSelect={(code) => {
              setTariff(code);
              setTerms(clubPrices[code] ?? null);
            }}
            onContinue={() => void openTerms(tariff)}
          />
        );

      case 'CLUB-TERMS':
        if (!terms) return <ScreenSkeleton />;

        return (
          <O.ClubTerms
            screen={screens['CLUB-TERMS']!}
            terms={terms}
            busy={payBusy}
            onPay={() => void payForClub()}
            onBack={() => setView({ id: 'CLUB-TARIFF' })}
          />
        );

      case 'CLUB-MINE':
        return (
          <O.ClubMine
            screen={screens['CLUB-MINE']!}
            subscriptions={subscriptions}
            busy={payBusy}
            onCancel={(id) => void cancelRenewal(id)}
            onBack={() => setView({ id: 'CLUB-01' })}
          />
        );

      case 'CLUB-WAIT':
        return (
          <O.ContentScreen
            screen={screens['CLUB-WAIT']!}
            actions={[
              { key: 'ok', label: 'Хорошо', kind: 'primary', onClick: () => void openMyResult() },
            ]}
          />
        );

      // ─── Из меню ──────────────────────────────────────────

      case 'MY-RESULT':
        return (
          <O.MyResult
            screen={screens['MY-RESULT']!}
            measurements={myMeasurements}
            notes={myNotes}
            dayBase={myDayBase}
            onContinue={() => void openObservation()}
            onStartOver={() => setView({ id: 'S-01' })}
          />
        );

      case 'SAFETY':
        return (
          <O.ContentScreen
            screen={screens.SAFETY!}
            actions={[
              {
                key: 'read',
                label: 'Прочитать',
                kind: 'primary',
                onClick: () => void openMyResult(),
              },
            ]}
          >
            <div className="block-stop">
              <ul className="dash-list">
                {(screening?.flags ?? []).map((flag) => (
                  <li key={flag}>{flag}</li>
                ))}
              </ul>
            </div>
          </O.ContentScreen>
        );

      case 'LEGAL':
        return (
          <O.ContentScreen
            screen={screens.LEGAL!}
            actions={[
              {
                key: 'offer',
                label: 'Оферта',
                kind: 'primary',
                onClick: () => void openLegal('LEGAL-OFFER'),
              },
              {
                key: 'privacy',
                label: 'Политика конфиденциальности',
                kind: 'secondary',
                onClick: () => void openLegal('LEGAL-PRIVACY'),
              },
            ]}
          />
        );

      case 'LEGAL-OFFER':
      case 'LEGAL-PRIVACY': {
        const loaded = screens[v.id];
        if (!loaded) return <ScreenSkeleton />;

        return <O.LegalDocument screen={loaded} onBack={() => setView({ id: 'LEGAL' })} />;
      }

      case 'DAY':
        return (
          <D.DayForm
            screen={screens.DAY!}
            today={today?.localDate ?? new Date().toISOString().slice(0, 10)}
            entry={today?.entry ?? null}
            busy={dayBusy}
            onSave={(entry) => {
              setDayBusy(true);
              void api
                .saveDay(entry)
                .then(() => openDayLog())
                .catch(handle)
                .finally(() => setDayBusy(false));
            }}
            onBack={() => void openHome()}
          />
        );

      case 'DAY-LOG':
        return (
          <D.DayLog
            screen={screens['DAY-LOG']!}
            week={dayWeek}
            calendar={dayCalendar}
            month={dayMonth}
            earliest={dayEarliest}
            onMonth={(m) => void loadMonth(m).catch(handle)}
            onToday={() => void openDay().catch(handle)}
            onBack={() => void openHome()}
          />
        );

      case 'ASK':
        return (
          <O.Ask
            screen={screens.ASK!}
            defaultName={firstName}
            busy={askBusy}
            onSend={(input) => {
              setAskBusy(true);
              void api
                .askQuestion(input)
                .then(() => setView({ id: 'ASK-OK' }))
                .catch(handle)
                .finally(() => setAskBusy(false));
            }}
            onBack={() => void openHome()}
          />
        );

      case 'ASK-OK':
        return (
          <O.ContentScreen
            screen={screens['ASK-OK']!}
            actions={[
              { key: 'ok', label: 'Хорошо', kind: 'primary', onClick: () => void openHome() },
            ]}
          />
        );

      case 'SUPPORT':
        return (
          <O.ContentScreen
            screen={screens.SUPPORT!}
            actions={[
              {
                key: 'write_support',
                label: 'Написать в поддержку',
                kind: 'primary',
                onClick: () => {
                  if (links.support) window.open(links.support, '_blank', 'noopener');
                },
              },
              {
                key: 'delete_data',
                label: 'Удалить мои данные',
                kind: 'tertiary',
                onClick: () => {
                  // Подтверждение в два шага: удаление необратимо.
                  if (!window.confirm('Удалить профиль и все замеры? Это нельзя отменить.')) return;
                  void api
                    .requestDeletion()
                    .then(() => api.confirmDeletion())
                    .then(() => window.location.reload())
                    .catch(handle);
                },
              },
            ]}
          />
        );

      case 'MORNING':
        return (
          <O.MorningCheck
            screen={screens.MORNING!}
            onAnswer={(choice) =>
              void api
                .answerMorningCheck(choice as never)
                .then(() => void openMyResult())
                .catch(handle)
            }
          />
        );

      default:
        return <ScreenSkeleton />;
    }
  }
}
